import { createStyles } from 'antd-style';

import { ThemeColors, ThemeFont, hexToRgbaWithAlpha } from '../../theme';
import { flexCenter } from '../../styles/mixins';

const CIRCLE_SIZE = 30;
const OUTER_CIRCLE_SIZE = 40;
const BORDER_COLOR = ThemeColors.COLOR_GREY7_7;
const BACKGROUND_COLOR = ThemeColors.COLOR_GREY9_9;
const BOX_SHADOW = `0 4px 6px ${hexToRgbaWithAlpha(ThemeColors.COLOR_GREY8_7, 0.65)}`;

const safariSafeZIndex = value => ({
  WebkitTransform: 'translate3d(0, 0, 0)',
  zIndex: value,
});

export const useMilestoneStepperStyles = createStyles(({ token, css, cx }) => {
  const alignment = cx(
    css({
      transform: 'translate(-50%)',
      textAlign: 'center',

      '&.secondary': {
        transform: 'translate(-50%, 125%)',
        fontSize: 12,
        fontWeight: 400,
      },
    })
  );
  const circleStepInner = cx(
    css({
      position: 'absolute',
      ...flexCenter(),
      width: CIRCLE_SIZE,
      height: CIRCLE_SIZE,
      backgroundImage: `linear-gradient(180deg, ${ThemeColors.COLOR_GREY8} 0%, ${ThemeColors.COLOR_GREY4} 100%)`,
      border: `1px solid ${ThemeColors.COLOR_GREY4}`,
      borderRadius: '100%',
      fontSize: 16,
      color: token.colorWhite,
      ...safariSafeZIndex(200),
    })
  );
  const rectStepInner = cx(
    css({
      position: 'absolute',
      width: 10,
      height: 14,
      backgroundColor: ThemeColors.COLOR_GREY3,
      borderRadius: 4,
      ...safariSafeZIndex(200),
    })
  );

  return {
    container: {
      margin: `0 ${OUTER_CIRCLE_SIZE / 2}px`,
    },
    timelineSection: {
      height: 50,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    titles: {
      lineHeight: '1.2em',
      whiteSpace: 'nowrap',
      fontFamily: token.fontFamily,
      fontSize: token.fontSize,
      fontWeight: 500,
      height: 18,
      color: token.colorText,
    },
    subtitles: {
      height: '1.2em',
      lineHeight: '1.2em',
      whiteSpace: 'nowrap',
      fontSize: ThemeFont.FONT_SIZE_SM_PLUS,
      fontWeight: 'normal',
      color: ThemeColors.COLOR_GREY_BLUISH,
    },
    descriptions: {
      height: '1.3em',
      lineHeight: '1.3em',
      whiteSpace: 'nowrap',
      fontSize: token.fontSizeSM,
      color: ThemeColors.COLOR_GREY_BLUISH,
    },
    alignedTexts: {
      position: 'relative',
    },
    position: {
      position: 'absolute',

      [`&:first-child .${alignment}`]: {
        transform: `translate(-${CIRCLE_SIZE / 2}px)`,
        textAlign: 'left',
      },
      [`&:last-child .${alignment}`]: {
        transform: `translate(calc(-100% + ${CIRCLE_SIZE / 2}px))`,
        textAlign: 'right',
      },
    },
    alignment,
    timeline: {
      ...safariSafeZIndex(1),
      position: 'relative',
      width: '100%',
      height: 18,
      backgroundColor: BACKGROUND_COLOR,
      color: token.colorWhite,
      border: `1px solid ${BORDER_COLOR}`,
      boxShadow: BOX_SHADOW,
      ...flexCenter(),

      '&::before': {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        content: '""',
        backgroundColor: BACKGROUND_COLOR,
        ...safariSafeZIndex(100),
      },
    },
    step: {
      position: 'absolute',
      top: '50%',
    },
    circleStep: {
      ...flexCenter(),

      '&.active': {
        [`> .${circleStepInner}`]: {
          backgroundImage: `linear-gradient(180deg, ${ThemeColors.COLOR_RED3} 0%, ${ThemeColors.COLOR_RED4} 100%)`,
          border: `1px solid ${ThemeColors.COLOR_RED5}`,
        },
      },
    },
    circleStepOuter: {
      position: 'absolute',
      width: OUTER_CIRCLE_SIZE,
      height: OUTER_CIRCLE_SIZE,
      backgroundColor: BACKGROUND_COLOR,
      borderRadius: '100%',
      border: `1px solid ${BORDER_COLOR}`,
      boxShadow: BOX_SHADOW,
      ...safariSafeZIndex(0),
    },
    circleStepInner,
    rectStep: {
      ...flexCenter(),

      '&.active': {
        [`> .${rectStepInner}`]: {
          height: 16,
          width: 12,
          border: `2px solid ${token.colorPrimary}`,
          backgroundColor: token.colorWhite,
        },
      },
      '&.deactivated': {
        [`> .${rectStepInner}`]: {
          height: 16,
          width: 12,
          border: `2px solid ${token.colorWhite}`,
        },
      },
    },
    rectStepInner,
    progressBar: {
      backgroundColor: token.colorPrimary,
      borderTop: `1px solid ${ThemeColors.COLOR_RED5}`,
      borderBottom: `1px solid ${ThemeColors.COLOR_RED5}`,
      position: 'absolute',
      height: 10,
      left: 0,
      ...safariSafeZIndex(110),
    },
  };
});
